import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  Clock, Users, Scissors, Calendar, ChevronRight, ChevronLeft,
  Plus, Edit2, Trash2, Save, X, Euro, Camera, Upload, CalendarDays
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============= SERVICES TAB =============
const ServicesTab = () => {
  const [services, setServices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingTemplate, setAddingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration_minutes: 30,
    price_gross: 0,
    is_active: true
  });

  useEffect(() => {
    loadServices();
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await api.get('/reseller/service-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates', error);
    }
  };

  const handleAddFromTemplate = async (tpl) => {
    setAddingTemplate(tpl.key);
    try {
      const res = await api.post('/reseller/services/from-template', {
        name: tpl.name,
        duration_minutes: tpl.duration_minutes,
        price_gross: tpl.price_gross,
        description: tpl.description,
      });
      if (res.data.skipped) {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message);
        loadServices();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    } finally {
      setAddingTemplate(null);
    }
  };

  const loadServices = async () => {
    try {
      const response = await api.get('/reseller/services');
      setServices(response.data);
    } catch (error) {
      console.error('Failed to load services', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await api.put(`/reseller/services/${editingService.id}`, formData);
        toast.success('Service aktualisiert');
      } else {
        await api.post('/reseller/services', formData);
        toast.success('Service erstellt');
      }
      loadServices();
      resetForm();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Service wirklich löschen?')) return;
    try {
      await api.delete(`/reseller/services/${id}`);
      toast.success('Service gelöscht');
      loadServices();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      duration_minutes: 30,
      price_gross: 0,
      is_active: true
    });
    setEditingService(null);
    setShowAddForm(false);
  };

  const startEdit = (service) => {
    setFormData({
      name: service.name,
      description: service.description || '',
      duration_minutes: service.duration_minutes,
      price_gross: service.price_gross ?? service.price ?? 0,
      is_active: service.is_active
    });
    setEditingService(service);
    setShowAddForm(true);
  };

  if (loading) return <div className="p-4">Laden...</div>;

  return (
    <div className="space-y-6">
      {templates.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="service-templates-panel">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Schnell hinzufügen — Klicke ein Angebot an
          </h3>
          <div className="flex flex-wrap gap-2">
            {templates.map(tpl => (
              <button
                key={tpl.key}
                type="button"
                disabled={addingTemplate === tpl.key}
                onClick={() => handleAddFromTemplate(tpl)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 hover:bg-primary hover:text-white hover:border-primary text-sm transition-colors disabled:opacity-50"
                data-testid={`template-chip-${tpl.key}`}
              >
                <Plus className="w-3 h-3" />
                <span>{tpl.name}</span>
                <span className="text-xs opacity-70">
                  {tpl.duration_minutes} Min · {tpl.price_gross.toFixed(0)} €
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Dienstleistungen</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800"
          data-testid="add-service-btn"
        >
          <Plus className="w-4 h-4" />
          Neuer Service
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 p-4 rounded-lg space-y-4" data-testid="service-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full border rounded-lg px-3 py-2"
                required
                data-testid="service-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Preis (€) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price_gross}
                onChange={(e) => setFormData({...formData, price_gross: parseFloat(e.target.value)})}
                className="w-full border rounded-lg px-3 py-2"
                required
                data-testid="service-price-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Dauer (Minuten) *</label>
              <select
                value={formData.duration_minutes}
                onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                className="w-full border rounded-lg px-3 py-2"
                data-testid="service-duration-select"
              >
                <option value={15}>15 Min</option>
                <option value={30}>30 Min</option>
                <option value={45}>45 Min</option>
                <option value={60}>60 Min</option>
                <option value={90}>90 Min</option>
                <option value={120}>120 Min</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Beschreibung</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full border rounded-lg px-3 py-2"
                data-testid="service-description-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
            />
            <label htmlFor="is_active" className="text-sm">Aktiv</label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800" data-testid="save-service-btn">
              <Save className="w-4 h-4 inline mr-2" />
              Speichern
            </button>
            <button type="button" onClick={resetForm} className="bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300">
              <X className="w-4 h-4 inline mr-2" />
              Abbrechen
            </button>
          </div>
        </form>
      )}

      <div className="grid gap-4">
        {services.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Noch keine Services angelegt</p>
        ) : (
          services.map((service) => (
            <div 
              key={service.id} 
              className={`bg-white border rounded-lg p-4 flex justify-between items-center ${!service.is_active ? 'opacity-50' : ''}`}
              data-testid={`service-item-${service.id}`}
            >
              <div>
                <h3 className="font-medium">{service.name}</h3>
                <p className="text-sm text-gray-500">
                  {service.duration_minutes} Min • {Number(service.price_gross ?? service.price ?? 0).toFixed(2)} €
                </p>
                {service.description && (
                  <p className="text-sm text-gray-400 mt-1">{service.description}</p>
                )}
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => startEdit(service)}
                  className="p-2 hover:bg-gray-100 rounded"
                  data-testid={`edit-service-${service.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDelete(service.id)}
                  className="p-2 hover:bg-red-50 text-red-500 rounded"
                  data-testid={`delete-service-${service.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============= STAFF TAB =============
const StaffTab = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStaff, setEditingStaff] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const photoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    email: '',
    phone: '',
    is_active: true,
    working_hours: {}
  });
  const [showHoursEditor, setShowHoursEditor] = useState(false);
  
  const DAYS = [
    { idx: 0, label: 'Montag' },
    { idx: 1, label: 'Dienstag' },
    { idx: 2, label: 'Mittwoch' },
    { idx: 3, label: 'Donnerstag' },
    { idx: 4, label: 'Freitag' },
    { idx: 5, label: 'Samstag' },
    { idx: 6, label: 'Sonntag' },
  ];
  
  const updateDayHours = (dayIdx, key, val) => {
    setFormData(prev => ({
      ...prev,
      working_hours: {
        ...(prev.working_hours || {}),
        [dayIdx]: {
          is_working: true,
          start: '09:00',
          end: '18:00',
          ...(prev.working_hours?.[dayIdx] || {}),
          [key]: val
        }
      }
    }));
  };

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      const response = await api.get('/reseller/staff');
      setStaff(response.data);
    } catch (error) {
      console.error('Failed to load staff', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (staffId, file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Nur Bilder erlaubt (JPEG, PNG, WebP)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 5 MB)');
      return;
    }
    
    setUploadingPhoto(staffId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/reseller/staff/${staffId}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Foto hochgeladen');
      loadStaff();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Hochladen';
      toast.error(msg);
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        await api.put(`/reseller/staff/${editingStaff.id}`, formData);
        toast.success('Mitarbeiter aktualisiert');
      } else {
        await api.post('/reseller/staff', formData);
        toast.success('Mitarbeiter erstellt');
      }
      loadStaff();
      resetForm();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Mitarbeiter wirklich löschen?')) return;
    try {
      await api.delete(`/reseller/staff/${id}`);
      toast.success('Mitarbeiter gelöscht');
      loadStaff();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      email: '',
      phone: '',
      is_active: true,
      working_hours: {}
    });
    setEditingStaff(null);
    setShowAddForm(false);
    setShowHoursEditor(false);
  };

  const startEdit = (member) => {
    setFormData({
      name: member.name,
      title: member.title || '',
      email: member.email || '',
      phone: member.phone || '',
      is_active: member.is_active,
      working_hours: member.working_hours || {}
    });
    setEditingStaff(member);
    setShowAddForm(true);
    setShowHoursEditor(false);
  };

  if (loading) return <div className="p-4">Laden...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Mitarbeiter</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800"
          data-testid="add-staff-btn"
        >
          <Plus className="w-4 h-4" />
          Neuer Mitarbeiter
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 p-4 rounded-lg space-y-4" data-testid="staff-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full border rounded-lg px-3 py-2"
                required
                data-testid="staff-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Titel/Position</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="z.B. Senior Barber"
                data-testid="staff-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">E-Mail</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full border rounded-lg px-3 py-2"
                data-testid="staff-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefon</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full border rounded-lg px-3 py-2"
                data-testid="staff-phone-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="staff_is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
            />
            <label htmlFor="staff_is_active" className="text-sm">Aktiv</label>
          </div>
          
          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setShowHoursEditor(!showHoursEditor)}
              className="text-sm text-primary hover:underline"
              data-testid="toggle-staff-hours"
            >
              {showHoursEditor ? '▼' : '▶'} Individuelle Arbeitszeiten {showHoursEditor ? 'ausblenden' : 'einstellen'} (optional)
            </button>
            {showHoursEditor && (
              <div className="mt-3 space-y-2 bg-white p-3 rounded border" data-testid="staff-hours-editor">
                <p className="text-xs text-gray-500 mb-2">
                  Wenn leer, gelten die Shop-Öffnungszeiten. Wenn gesetzt, werden diese Zeiten
                  bei der Kundenbuchung als verfügbare Slots des Mitarbeiters verwendet.
                </p>
                {DAYS.map(d => {
                  const hrs = formData.working_hours?.[d.idx] || {};
                  const enabled = !!(hrs.is_working !== false && (hrs.is_working || hrs.start || hrs.end));
                  return (
                    <div key={d.idx} className="flex items-center gap-3 text-sm" data-testid={`staff-hours-day-${d.idx}`}>
                      <label className="flex items-center gap-2 w-32">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => updateDayHours(d.idx, 'is_working', e.target.checked)}
                        />
                        <span>{d.label}</span>
                      </label>
                      {enabled && (
                        <>
                          <input
                            type="time"
                            value={hrs.start || '09:00'}
                            onChange={(e) => updateDayHours(d.idx, 'start', e.target.value)}
                            className="border rounded px-2 py-1"
                          />
                          <span>bis</span>
                          <input
                            type="time"
                            value={hrs.end || '18:00'}
                            onChange={(e) => updateDayHours(d.idx, 'end', e.target.value)}
                            className="border rounded px-2 py-1"
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="flex gap-2">
            <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800" data-testid="save-staff-btn">
              <Save className="w-4 h-4 inline mr-2" />
              Speichern
            </button>
            <button type="button" onClick={resetForm} className="bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300">
              <X className="w-4 h-4 inline mr-2" />
              Abbrechen
            </button>
          </div>
        </form>
      )}

      <div className="grid gap-4">
        {staff.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Noch keine Mitarbeiter angelegt</p>
        ) : (
          staff.map((member) => (
            <div 
              key={member.id} 
              className={`bg-white border rounded-lg p-4 flex justify-between items-center ${!member.is_active ? 'opacity-50' : ''}`}
              data-testid={`staff-item-${member.id}`}
            >
              <div className="flex items-center gap-4">
                {/* Photo with upload option */}
                <div className="relative group">
                  <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                    {member.image_url ? (
                      <img 
                        src={`${API_URL}/api/files/${member.image_url}`} 
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Users className="w-7 h-7 text-gray-500" />
                    )}
                  </div>
                  {/* Upload overlay */}
                  <label 
                    className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity"
                    data-testid={`upload-photo-${member.id}`}
                  >
                    {uploadingPhoto === member.id ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5 text-white" />
                    )}
                    <input 
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handlePhotoUpload(member.id, e.target.files?.[0])}
                      disabled={uploadingPhoto === member.id}
                    />
                  </label>
                </div>
                <div>
                  <h3 className="font-medium">{member.name}</h3>
                  {member.title && <p className="text-sm text-gray-500">{member.title}</p>}
                  {member.email && <p className="text-xs text-gray-400">{member.email}</p>}
                </div>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => startEdit(member)}
                  className="p-2 hover:bg-gray-100 rounded"
                  data-testid={`edit-staff-${member.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDelete(member.id)}
                  className="p-2 hover:bg-red-50 text-red-500 rounded"
                  data-testid={`delete-staff-${member.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============= BUSINESS HOURS TAB =============
const BusinessHoursTab = () => {
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDay, setEditingDay] = useState(null);

  useEffect(() => {
    loadHours();
  }, []);

  const loadHours = async () => {
    try {
      const response = await api.get('/reseller/business-hours');
      setHours(response.data);
    } catch (error) {
      console.error('Failed to load business hours', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (dayOfWeek, updates) => {
    try {
      await api.put(`/reseller/business-hours/${dayOfWeek}`, updates);
      toast.success('Öffnungszeiten aktualisiert');
      loadHours();
      setEditingDay(null);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  if (loading) return <div className="p-4">Laden...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Öffnungszeiten</h2>
      
      <div className="space-y-2">
        {hours.map((day) => (
          <div 
            key={day.day_of_week} 
            className="bg-white border rounded-lg p-4"
            data-testid={`hours-day-${day.day_of_week}`}
          >
            {editingDay === day.day_of_week ? (
              <EditHoursForm 
                day={day} 
                onSave={(updates) => handleUpdate(day.day_of_week, updates)}
                onCancel={() => setEditingDay(null)}
              />
            ) : (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <span className="font-medium w-28">{day.day_name}</span>
                  {day.is_open ? (
                    <span className="text-gray-600">
                      {day.open_time} - {day.close_time} Uhr
                      {day.break_start && day.break_end && (
                        <span className="text-gray-400 ml-2">
                          (Pause: {day.break_start} - {day.break_end})
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-red-500">Geschlossen</span>
                  )}
                </div>
                <button 
                  onClick={() => setEditingDay(day.day_of_week)}
                  className="p-2 hover:bg-gray-100 rounded"
                  data-testid={`edit-hours-${day.day_of_week}`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const EditHoursForm = ({ day, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    is_open: day.is_open,
    open_time: day.open_time,
    close_time: day.close_time,
    break_start: day.break_start || '',
    break_end: day.break_end || ''
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <span className="font-medium w-28">{day.day_name}</span>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.is_open}
            onChange={(e) => setFormData({...formData, is_open: e.target.checked})}
          />
          <span className="text-sm">Geöffnet</span>
        </label>
      </div>
      
      {formData.is_open && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 ml-32">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Öffnung</label>
            <input
              type="time"
              value={formData.open_time}
              onChange={(e) => setFormData({...formData, open_time: e.target.value})}
              className="border rounded px-2 py-1 w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Schließung</label>
            <input
              type="time"
              value={formData.close_time}
              onChange={(e) => setFormData({...formData, close_time: e.target.value})}
              className="border rounded px-2 py-1 w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Pause von</label>
            <input
              type="time"
              value={formData.break_start}
              onChange={(e) => setFormData({...formData, break_start: e.target.value})}
              className="border rounded px-2 py-1 w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Pause bis</label>
            <input
              type="time"
              value={formData.break_end}
              onChange={(e) => setFormData({...formData, break_end: e.target.value})}
              className="border rounded px-2 py-1 w-full"
            />
          </div>
        </div>
      )}
      
      <div className="flex gap-2 ml-32">
        <button 
          onClick={() => onSave(formData)}
          className="bg-black text-white px-3 py-1 rounded text-sm hover:bg-gray-800"
        >
          Speichern
        </button>
        <button 
          onClick={onCancel}
          className="bg-gray-200 px-3 py-1 rounded text-sm hover:bg-gray-300"
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
};

// ============= WEEK VIEW TAB (Team-Wochenansicht) =============
const WeekViewTab = () => {
  const [staff, setStaff] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week

  // Compute the Monday of the target week
  const getMonday = (offset) => {
    const now = new Date();
    const day = now.getDay(); // 0 = Sun
    const diff = day === 0 ? -6 : 1 - day; // shift to Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff + offset * 7);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  const monday = getMonday(weekOffset);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });

  const fmtDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  const loadData = async () => {
    setLoading(true);
    try {
      const dateFrom = fmtDate(weekDays[0]);
      const dateTo = fmtDate(weekDays[6]);
      const [staffRes, apptRes] = await Promise.all([
        api.get('/reseller/staff'),
        api.get(`/reseller/appointments?date_from=${dateFrom}&date_to=${dateTo}`)
      ]);
      setStaff((staffRes.data || []).filter(s => s.is_active !== false));
      setAppointments(apptRes.data || []);
    } catch (error) {
      console.error('Failed to load week data', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [weekOffset]);

  // Hours from 08:00 to 20:00 (30-min slots)
  const HOURS = [];
  for (let h = 8; h < 20; h++) {
    HOURS.push(`${String(h).padStart(2, '0')}:00`);
    HOURS.push(`${String(h).padStart(2, '0')}:30`);
  }

  const parseTime = (t) => {
    const [hh, mm] = (t || '00:00').split(':').map(Number);
    return hh * 60 + mm;
  };

  // Build a map: dayIdx -> staffId -> appointments[]
  const buildAppointmentsIndex = () => {
    const idx = {};
    for (const appt of appointments) {
      if (appt.status === 'cancelled') continue;
      const dayIdx = weekDays.findIndex(d => fmtDate(d) === appt.appointment_date);
      if (dayIdx === -1) continue;
      const staffKey = appt.staff_id || 'unassigned';
      if (!idx[dayIdx]) idx[dayIdx] = {};
      if (!idx[dayIdx][staffKey]) idx[dayIdx][staffKey] = [];
      idx[dayIdx][staffKey].push(appt);
    }
    return idx;
  };

  const apptIndex = buildAppointmentsIndex();

  const staffColors = [
    'bg-blue-100 border-blue-400 text-blue-900',
    'bg-emerald-100 border-emerald-400 text-emerald-900',
    'bg-amber-100 border-amber-400 text-amber-900',
    'bg-purple-100 border-purple-400 text-purple-900',
    'bg-rose-100 border-rose-400 text-rose-900',
    'bg-teal-100 border-teal-400 text-teal-900',
    'bg-indigo-100 border-indigo-400 text-indigo-900',
  ];

  const getStaffColor = (staffId) => {
    if (!staffId) return 'bg-gray-100 border-gray-400 text-gray-900';
    const idx = staff.findIndex(s => s.id === staffId);
    return staffColors[idx % staffColors.length] || staffColors[0];
  };

  const getStaffName = (staffId) => {
    if (!staffId) return 'Nicht zugewiesen';
    return staff.find(s => s.id === staffId)?.name || '—';
  };

  // Compute utilization per staff for the whole week
  const computeUtilization = () => {
    const util = {};
    for (const s of staff) util[s.id] = 0;
    util['unassigned'] = 0;
    for (const appt of appointments) {
      if (appt.status === 'cancelled') continue;
      const dur = appt.duration_minutes || 30;
      const key = appt.staff_id || 'unassigned';
      util[key] = (util[key] || 0) + dur;
    }
    return util;
  };

  const utilization = computeUtilization();

  const weekLabel = `${weekDays[0].toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} – ${weekDays[6].toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;

  if (loading) return <div className="p-4">Laden...</div>;

  return (
    <div className="space-y-4" data-testid="week-view-tab">
      {/* Header controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <CalendarDays className="w-5 h-5" />
          Team-Wochenansicht
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setWeekOffset(weekOffset - 1)}
            className="p-2 hover:bg-gray-100 rounded"
            data-testid="week-prev-btn"
            aria-label="Vorherige Woche"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            className="px-3 py-1 text-sm border rounded hover:bg-gray-100"
            data-testid="week-today-btn"
          >
            Heute
          </button>
          <div className="px-3 text-sm font-medium min-w-[180px] text-center" data-testid="week-label">
            {weekLabel}
          </div>
          <button
            onClick={() => setWeekOffset(weekOffset + 1)}
            className="p-2 hover:bg-gray-100 rounded"
            data-testid="week-next-btn"
            aria-label="Nächste Woche"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Utilization bar */}
      {staff.length > 0 && (
        <div className="bg-white border rounded-lg p-4" data-testid="utilization-panel">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Auslastung diese Woche</h3>
          <div className="flex flex-wrap gap-3">
            {staff.map(s => {
              const minutes = utilization[s.id] || 0;
              const hours = (minutes / 60).toFixed(1);
              return (
                <div
                  key={s.id}
                  className={`px-3 py-2 rounded-lg border ${getStaffColor(s.id)}`}
                  data-testid={`utilization-${s.id}`}
                >
                  <div className="text-xs opacity-70">{s.name}</div>
                  <div className="font-bold">{hours} h</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {staff.length === 0 ? (
        <p className="text-gray-500 text-center py-8">
          Noch keine Mitarbeiter angelegt. Legen Sie zuerst Mitarbeiter im Tab &bdquo;Mitarbeiter&ldquo; an.
        </p>
      ) : (
        // Week grid: 7 columns (days), inside each day columns for staff
        <div className="overflow-x-auto border rounded-lg bg-white">
          <div className="grid" style={{ gridTemplateColumns: `70px repeat(${weekDays.length}, minmax(${Math.max(staff.length * 110, 140)}px, 1fr))` }}>
            {/* Header row */}
            <div className="border-b border-r bg-gray-50 p-2 text-xs font-semibold text-gray-500 sticky left-0 z-10">Zeit</div>
            {weekDays.map((d, dayIdx) => (
              <div key={dayIdx} className="border-b border-r bg-gray-50 p-2" data-testid={`week-day-header-${dayIdx}`}>
                <div className="text-xs text-gray-500">{DAY_LABELS[dayIdx]}</div>
                <div className="text-sm font-semibold">{d.getDate()}.{d.getMonth() + 1}.</div>
                {/* Staff subheader */}
                <div className="mt-1 grid gap-1" style={{ gridTemplateColumns: `repeat(${staff.length}, 1fr)` }}>
                  {staff.map(s => (
                    <div key={s.id} className={`text-[10px] px-1 py-0.5 rounded truncate ${getStaffColor(s.id)}`} title={s.name}>
                      {s.name.split(' ')[0]}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Hour rows */}
            {HOURS.map((timeSlot, rowIdx) => {
              const slotMin = parseTime(timeSlot);
              return (
                <React.Fragment key={timeSlot}>
                  <div className="border-b border-r p-2 text-xs text-gray-500 sticky left-0 z-10 bg-white">
                    {timeSlot}
                  </div>
                  {weekDays.map((d, dayIdx) => (
                    <div
                      key={dayIdx}
                      className="border-b border-r p-1 min-h-[42px] grid gap-1"
                      style={{ gridTemplateColumns: `repeat(${staff.length}, 1fr)` }}
                    >
                      {staff.map(s => {
                        const dayAppts = apptIndex[dayIdx]?.[s.id] || [];
                        const matching = dayAppts.find(a => {
                          const start = parseTime(a.appointment_time);
                          const end = start + (a.duration_minutes || 30);
                          return start <= slotMin && slotMin < end;
                        });
                        if (!matching) {
                          return <div key={s.id} className="rounded" />;
                        }
                        // Only render the block on the first slot of the appointment
                        const isFirstSlot = parseTime(matching.appointment_time) === slotMin;
                        if (!isFirstSlot) {
                          return (
                            <div key={s.id} className={`${getStaffColor(s.id)} border-l-2 opacity-70 rounded-none text-[10px] px-1 flex items-center overflow-hidden`}>
                              …
                            </div>
                          );
                        }
                        const spanSlots = Math.ceil((matching.duration_minutes || 30) / 30);
                        return (
                          <div
                            key={s.id}
                            className={`${getStaffColor(s.id)} border-l-2 rounded px-1 py-0.5 text-[10px] overflow-hidden`}
                            style={{ minHeight: `${spanSlots * 42 - 4}px` }}
                            data-testid={`appt-block-${matching.id}`}
                            title={`${matching.customer_name} – ${matching.service_name}`}
                          >
                            <div className="font-semibold truncate">{matching.appointment_time}</div>
                            <div className="truncate">{matching.customer_name}</div>
                            <div className="truncate opacity-80">{matching.service_name}</div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ============= APPOINTMENTS TAB =============
const AppointmentsTab = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadAppointments();
  }, [statusFilter]);

  const loadAppointments = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await api.get(`/reseller/appointments${params}`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to load appointments', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, reason = null) => {
    try {
      await api.put(`/reseller/appointments/${id}/status`, { 
        status,
        cancellation_reason: reason 
      });
      toast.success('Status aktualisiert');
      loadAppointments();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleCancel = async (id) => {
    const reason = window.prompt('Grund für Stornierung (optional):');
    if (reason !== null) {
      await updateStatus(id, 'cancelled', reason);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      no_show: 'bg-yellow-100 text-yellow-800'
    };
    const labels = {
      confirmed: 'Bestätigt',
      cancelled: 'Storniert',
      completed: 'Abgeschlossen',
      no_show: 'Nicht erschienen'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) return <div className="p-4">Laden...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Termine</h2>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded-lg px-3 py-2"
          data-testid="status-filter"
        >
          <option value="">Alle Status</option>
          <option value="confirmed">Bestätigt</option>
          <option value="completed">Abgeschlossen</option>
          <option value="cancelled">Storniert</option>
          <option value="no_show">Nicht erschienen</option>
        </select>
      </div>

      <div className="space-y-4">
        {appointments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Keine Termine gefunden</p>
        ) : (
          appointments.map((appt) => (
            <div 
              key={appt.id} 
              className="bg-white border rounded-lg p-4"
              data-testid={`appointment-item-${appt.id}`}
            >
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-lg">
                      {appt.appointment_date} • {appt.appointment_time} Uhr
                    </span>
                    {getStatusBadge(appt.status)}
                  </div>
                  <p className="font-medium">{appt.customer_name}</p>
                  <p className="text-sm text-gray-600">
                    {appt.customer_phone} • {appt.customer_email}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span><Scissors className="w-4 h-4 inline mr-1" />{appt.service_name}</span>
                    <span><Clock className="w-4 h-4 inline mr-1" />{appt.duration_minutes} Min</span>
                    <span><Euro className="w-4 h-4 inline mr-1" />{Number(appt.service_price ?? 0).toFixed(2)} €</span>
                    {appt.staff_name && <span><Users className="w-4 h-4 inline mr-1" />{appt.staff_name}</span>}
                  </div>
                  {appt.notes && <p className="text-sm text-gray-400">Notiz: {appt.notes}</p>}
                  {appt.cancellation_reason && (
                    <p className="text-sm text-red-500">Stornierungsgrund: {appt.cancellation_reason}</p>
                  )}
                </div>
                
                {appt.status === 'confirmed' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateStatus(appt.id, 'completed')}
                      className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200"
                      data-testid={`complete-${appt.id}`}
                    >
                      Abschließen
                    </button>
                    <button
                      onClick={() => handleCancel(appt.id)}
                      className="text-sm bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200"
                      data-testid={`cancel-${appt.id}`}
                    >
                      Stornieren
                    </button>
                    <button
                      onClick={() => updateStatus(appt.id, 'no_show')}
                      className="text-sm bg-yellow-100 text-yellow-700 px-3 py-1 rounded hover:bg-yellow-200"
                      data-testid={`noshow-${appt.id}`}
                    >
                      No-Show
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============= MAIN PAGE =============
const MyShopAppointmentsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('appointments');

  const tabs = [
    { id: 'appointments', label: 'Termine', icon: Calendar },
    { id: 'week', label: 'Wochenansicht', icon: CalendarDays },
    { id: 'services', label: 'Dienstleistungen', icon: Scissors },
    { id: 'staff', label: 'Mitarbeiter', icon: Users },
    { id: 'hours', label: 'Öffnungszeiten', icon: Clock },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="my-shop-appointments-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/my-shop" className="hover:text-black">Mein Shop</Link>
          <ChevronRight className="w-4 h-4" />
          <span>Terminbuchung</span>
        </div>

        <h1 className="text-3xl font-bold mb-8">Terminverwaltung</h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b pb-4 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-black text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          {activeTab === 'appointments' && <AppointmentsTab />}
          {activeTab === 'week' && <WeekViewTab />}
          {activeTab === 'services' && <ServicesTab />}
          {activeTab === 'staff' && <StaffTab />}
          {activeTab === 'hours' && <BusinessHoursTab />}
        </div>
      </div>
    </div>
  );
};

export default MyShopAppointmentsPage;
